import type { ScheduleBar } from '../types.js';
import { barStyleFor, needsAttention } from './barStyle.js';
import { MYT_OFFSET_MS, type ScheduleView, mytDateOf, rentalKeyOf } from './dropLogic.js';

/**
 * 368 (#1236, design A) — in Week and Month, the items that start and end within one Malaysia day collapse into one
 * cell per car-day: at those zooms a 3-hour rental is 2–5 px (Month) or 8–22 px with no text (Week), so a busy car's
 * day read as slivers. The cell says how many, how long, and whether any is unpaid or needs attention; it opens the
 * list. Multi-day items stay bars. Built from the bars already loaded; the server is unchanged.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DayCell {
  /** `cell:<car>:<YYYY-MM-DD>` — stable, so React and dnd-timeline keep the same item across renders. */
  id: string;
  carId: string;
  /** The Malaysia date. */
  date: string;
  /** The Malaysia day's bounds (ms): the cell is drawn across the whole day. */
  start: number;
  end: number;
  /** In start order. */
  items: ScheduleBar[];
  /** Distinct rentals (an extension leg belongs to its rental). */
  rentals: number;
  /** Blocks: maintenance, inspection, manual, other. */
  blocks: number;
  /** Booked hours of the rentals, their union, to the half hour. */
  hours: number;
  /** The day's covered share by every item, their union (0–1). */
  share: number;
  unpaid: boolean;
  attention: boolean;
}

const isRental = (b: ScheduleBar) => b.kind === 'booking' || b.kind === 'extension';

/** R1: starts and ends on the same Malaysia day; a midnight-to-midnight item counts, one crossing midnight does not. */
export function isWithinOneDay(bar: ScheduleBar): boolean {
  const start = Date.parse(bar.start_at);
  const end = Date.parse(bar.end_at);
  return end > start && mytDateOf(start) === mytDateOf(end - 1);
}

/** The total length of the union of spans (overlaps counted once). */
function unionMs(spans: Array<[number, number]>): number {
  const sorted = [...spans].sort((a, b) => a[0] - b[0]);
  let total = 0;
  let curStart = Number.NEGATIVE_INFINITY;
  let curEnd = Number.NEGATIVE_INFINITY;
  for (const [s, e] of sorted) {
    if (s > curEnd) {
      if (curEnd > curStart) total += curEnd - curStart;
      curStart = s;
      curEnd = e;
    } else if (e > curEnd) curEnd = e;
  }
  if (curEnd > curStart) total += curEnd - curStart;
  return total;
}

/** The Malaysia midnight (ms) that starts the day containing `ms`. */
function mytDayStart(ms: number): number {
  return Date.parse(`${mytDateOf(ms)}T00:00:00.000Z`) - MYT_OFFSET_MS;
}

/** FR-001: Week and Month collapse one-day items into cells; the Day view keeps every bar. */
export function splitDayCells(
  bars: readonly ScheduleBar[],
  view: ScheduleView,
): { bars: ScheduleBar[]; cells: DayCell[] } {
  if (view === 'day') return { bars: [...bars], cells: [] };
  const kept: ScheduleBar[] = [];
  const groups = new Map<string, ScheduleBar[]>();
  for (const b of bars) {
    if (!isWithinOneDay(b)) {
      kept.push(b);
      continue;
    }
    const key = `${b.car_id}\u0000${mytDateOf(Date.parse(b.start_at))}`;
    groups.set(key, [...(groups.get(key) ?? []), b]);
  }
  const cells: DayCell[] = [];
  for (const [key, list] of groups) {
    const [carId, date] = key.split('\u0000') as [string, string];
    const items = [...list].sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at));
    const start = mytDayStart(Date.parse(items[0]?.start_at ?? ''));
    const span = (b: ScheduleBar): [number, number] => [
      Date.parse(b.start_at),
      Date.parse(b.end_at),
    ];
    const rentalItems = items.filter(isRental);
    const rentalKeys = new Set(rentalItems.map((b) => rentalKeyOf(b) ?? b.id));
    cells.push({
      id: `cell:${carId}:${date}`,
      carId,
      date,
      start,
      end: start + DAY_MS,
      items,
      rentals: rentalKeys.size,
      blocks: items.length - rentalItems.length,
      hours: Math.round((unionMs(rentalItems.map(span)) / 3_600_000) * 2) / 2,
      share: unionMs(items.map(span)) / DAY_MS,
      unpaid: rentalItems.some((b) => barStyleFor(b).state === 'awaiting_payment'),
      attention: items.some(needsAttention),
    });
  }
  return { bars: kept, cells };
}
